import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { TailSpin } from 'react-loader-spinner';
import keycloak from './keycloak';

const KeycloakContext = createContext({
    initialized: false,
    authenticated: false,
    token: null,
    tokenParsed: null,
    error: null,
    login: async () => {},
    logout: async () => {},
    refreshToken: async () => false,
});

export const useKeycloak = () => {
    return useContext(KeycloakContext);
};

export const KeycloakProvider = ({ children }) => {
    const initializationStarted = useRef(false);

    const [initialized, setInitialized] = useState(false);
    const [authenticated, setAuthenticated] = useState(false);
    const [token, setToken] = useState(null);
    const [tokenParsed, setTokenParsed] = useState(null);
    const [error, setError] = useState(null);

    const synchronizeState = useCallback(() => {
        setAuthenticated(Boolean(keycloak.authenticated));
        setToken(keycloak.token || null);
        setTokenParsed(keycloak.tokenParsed || null);
    }, []);

    const refreshToken = useCallback(async (minimumValidity = 30) => {
        if (!keycloak.authenticated) {
            return false;
        }

        try {
            const refreshed = await keycloak.updateToken(minimumValidity);

            synchronizeState();

            return refreshed;
        } catch (refreshError) {
            console.error(
                'Não foi possível renovar o token Keycloak:',
                refreshError
            );

            setError(refreshError);
            setAuthenticated(false);
            setToken(null);
            setTokenParsed(null);

            return false;
        }
    }, [synchronizeState]);

    const login = useCallback(async (options = {}) => {
        await keycloak.login({
            redirectUri: window.location.href,
            ...options,
        });
    }, []);

    const logout = useCallback(async (options = {}) => {
        localStorage.removeItem('userID');
        localStorage.removeItem('name');
        localStorage.removeItem('email');

        await keycloak.logout({
            redirectUri: window.location.origin,
            ...options,
        });
    }, []);

    useEffect(() => {
        if (initializationStarted.current) {
            return;
        }

        initializationStarted.current = true;

        keycloak.onAuthSuccess = () => {
            synchronizeState();
        };

        keycloak.onAuthRefreshSuccess = () => {
            synchronizeState();
        };

        keycloak.onAuthRefreshError = () => {
            setAuthenticated(false);
            setToken(null);
            setTokenParsed(null);
        };

        keycloak.onAuthLogout = () => {
            setAuthenticated(false);
            setToken(null);
            setTokenParsed(null);
        };

        keycloak.onTokenExpired = async () => {
            const refreshed = await refreshToken(30);

            if (!refreshed && !keycloak.authenticated) {
                await login();
            }
        };

        const initializeKeycloak = async () => {
            try {
                const isAuthenticated = await keycloak.init({
                    onLoad: 'login-required',
                    pkceMethod: 'S256',
                    checkLoginIframe: false,
                    flow: 'standard',
                });

                setAuthenticated(isAuthenticated);
                synchronizeState();
            } catch (initializationError) {
                console.error(
                    'Erro ao inicializar o Keycloak:',
                    initializationError
                );

                setError(initializationError);
                setAuthenticated(false);
            } finally {
                setInitialized(true);
            }
        };

        initializeKeycloak();
    }, [login, refreshToken, synchronizeState]);

    useEffect(() => {
        if (!initialized || !authenticated) {
            return undefined;
        }

        const interval = window.setInterval(() => {
            refreshToken(60);
        }, 30000);

        return () => {
            window.clearInterval(interval);
        };
    }, [initialized, authenticated, refreshToken]);

    const contextValue = useMemo(() => ({
        initialized,
        authenticated,
        token,
        tokenParsed,
        error,
        login,
        logout,
        refreshToken,
        keycloak,
    }), [
        initialized,
        authenticated,
        token,
        tokenParsed,
        error,
        login,
        logout,
        refreshToken,
    ]);

    if (!initialized) {
        return (
            <div className="loading-container">
                <TailSpin
                    height="80"
                    width="80"
                    color="#0c1534"
                    ariaLabel="A autenticar"
                />
            </div>
        );
    }

    if (error && !authenticated) {
        return (
            <div className="login-container">
                <div className="login-box">
                    <h2>Erro de autenticação</h2>

                    <p>
                        Não foi possível iniciar a sessão no Olympus.
                    </p>

                    <button
                        type="button"
                        className="btn-primary"
                        onClick={() => login()}
                    >
                        TENTAR NOVAMENTE
                    </button>
                </div>
            </div>
        );
    }

    return (
        <KeycloakContext.Provider value={contextValue}>
            {children}
        </KeycloakContext.Provider>
    );
};

export default KeycloakContext;